import { object, string, array, bool } from 'yup';
import { treatmentRecordCommonSchema } from './treatmentRecordCommon';
import s3jsonConstants from '../../constants/s3jsonConstants';
import { facialTreatmentRecordCommonSchema } from './facialTreatmentRecordCommon';
import { nullToEmptyString, nullToArray } from './transforms';

const isScalpRevival = (maskEnhancements: string[] | undefined) =>
  maskEnhancements?.includes(s3jsonConstants['Soothing Scalp Revival'])
    ? true
    : maskEnhancements?.includes(s3jsonConstants['Soothing Scalp Revival']);

export const heydayTreatmentRecordSchema = object().shape({
  treatmentAttributes: object().shape({
    exfoliationLevel: string()
      .when(['exfoliationEnhancement', 'exfoliationSegment'], {
        is: (
          enhancement?: string[] | undefined,
          segment?: boolean | undefined,
        ) => (enhancement?.length === 0 || !enhancement) && !!segment,
        then: string().required('Exfoliation Level'),
      })
      .nullable(true),
    levelOneExfoliationBase: array(string())
      .when('exfoliationLevel', {
        is: (exfoliationLevel: string) =>
          exfoliationLevel === s3jsonConstants['Level 1 Exfoliation'],
        then: array(string())
          .required('Exfoliation Base')
          .min(1, 'Exfoliation Base'),
      })
      .nullable(true)
      .transform(nullToArray),
    levelOneExfoliationActive: string()
      .when('exfoliationLevel', {
        is: (exfoliationLevel: string) =>
          exfoliationLevel === s3jsonConstants['Level 1 Exfoliation'],
        then: string().required('Exfoliation Active'),
      })
      .nullable(true),
    levelTwoExfoliationBase: array(string())
      .when('exfoliationLevel', {
        is: (exfoliationLevel: string) =>
          exfoliationLevel === s3jsonConstants['Level 2 Exfoliation'],
        then: array(string())
          .required('Exfoliation Base')
          .min(1, 'Exfoliation Base'),
      })
      .nullable(true)
      .transform(nullToArray),
    levelTwoExfoliationActive: array(string())
      .when('exfoliationLevel', {
        is: (exfoliationLevel: string) =>
          exfoliationLevel === s3jsonConstants['Level 2 Exfoliation'],
        then: array(string())
          .required('Exfoliation Active')
          .length(2, 'Exfoliation Active'),
      })
      .nullable(true)
      .transform(nullToArray),
    levelThreeExfoliationActive: string()
      .when('exfoliationLevel', {
        is: (exfoliationLevel: string) =>
          exfoliationLevel === s3jsonConstants['Level 3 Exfoliation'],
        then: string().required('Exfoliation Active'),
      })
      .nullable(true),
    levelFourExfoliationActive: array(string())
      .when('exfoliationLevel', {
        is: (exfoliationLevel: string) =>
          exfoliationLevel === s3jsonConstants['Level 4 Exfoliation'],
        then: array(string())
          .required('Exfoliation Active')
          .length(2, 'Exfoliation Active'),
      })
      .nullable(true)
      .transform(nullToArray),
    extractionsPrepMethod: array(string())
      .when('extractionsSegment', {
        is: true,
        then: array(string())
          .required('Extraction Prep Method')
          .min(1, 'Extraction Prep Method'),
      })
      .nullable(true)
      .transform(nullToArray),
    extractionsMmpMassageBase: array(string())
      .when(['extractionsPrepMethod', 'extractionsMmpMassageBaseTp'], {
        is: (
          extractionsPrepMethod: string[] | undefined,
          extractionsMmpMassageBaseTp: string,
        ) =>
          extractionsPrepMethod?.includes(
            s3jsonConstants['Massage Method Prep'],
          ) && !extractionsMmpMassageBaseTp,
        then: array(string()).required('Massage Base').min(1, 'Massage Base'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    extractionsDmpProduct: array(string())
      .when('extractionsPrepMethod', {
        is: (extractionsPrepMethod: string[] | undefined) =>
          extractionsPrepMethod?.includes(
            s3jsonConstants['Desincrustation Method Prep'],
          ),
        then: array(string())
          .required('Desincrustation Product')
          .min(1, 'Desincrustation Product'),
      })
      .nullable(true)
      .transform(nullToArray),
    touchSegment: bool().nullable(true),
    touchSegmentMassageBase: array(string())
      .when(['touchSegment', 'touchSegmentMassageBaseTp'], {
        is: (touchSegment: boolean, touchSegmentMassageBaseTp: string) =>
          !touchSegmentMassageBaseTp && touchSegment,
        then: array(string())
          .required('Touch Point Massage Base')
          .min(1, 'Touch Point Massage Base'),
      })
      .nullable(true)
      .transform(nullToArray),
    maskBaseProduct: array(string())
      .when(['maskEnhancements', 'maskSegment', 'maskBaseProductTp'], {
        is: (
          enhancement: string[] | undefined,
          segment: boolean | undefined,
          maskBaseProductTp: string,
        ) =>
          (enhancement?.length === 0 || !enhancement) &&
          !!segment &&
          !maskBaseProductTp,
        then: array(string())
          .required('Mask Base Product')
          .min(1, 'Mask Base Product'),
      })
      .nullable()
      .transform(nullToArray),
    maskHwiWandProduct: array(string())
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: array(string()).required('Mask Enhancements Wand Product'),
      })
      .nullable()
      .transform(nullToArray),
    maskHwiSuctionRate: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: string().required('Mask Enhancements Wand Serum'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskMicrocurrentProduct: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(s3jsonConstants['Microcurrent — $80']),
        then: string().optional(),
      })
      .nullable(true)
      .transform(nullToEmptyString),

    maskNanoinfusionDeviceOrRoomNumber: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
        then: string().required('Nano Infusion Device or Room Number'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskNanoinfusionNanoMask: string()
      .when(['maskEnhancements', 'maskNanoinfusionNanoMaskTp'], {
        is: (
          maskEnhancements: string[] | undefined,
          maskNanoinfusionNanoMaskTp: string,
        ) =>
          maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']) &&
          !maskNanoinfusionNanoMaskTp,
        then: string().required('Nano Infusion Nano Mask'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskSbMask: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          isScalpRevival(maskEnhancements),
        then: string().required('Soothing Scalp Revival Mask'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskSbMicrocurrentLong: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          isScalpRevival(maskEnhancements),
        then: string().required('Soothing Scalp Revival Microcurrent Long'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskSbIntensity: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          isScalpRevival(maskEnhancements),
        then: string().optional(),
      })
      .nullable()
      .transform(nullToEmptyString),
    sosBaseProduct: string()
      .when(['sosSegment', 'sosBaseProductTp'], {
        is: (sosSegment: boolean, sosBaseProductTp: string) =>
          sosSegment && !sosBaseProductTp,
        then: string().required('SOS Base Product'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    moisturizerAndSpf: array(string())
      .when('rRMoisturizerAndSpfTp', {
        is: (val: string) => !val,
        then: array(string())
          .required('Moisturizer & SPF')
          .min(1, 'Moisturizer & SPF'),
      })
      .nullable()
      .transform(nullToArray),
    choose_one: bool().test(
      'atleast_at least one',
      'Choose at least one segment',
      function () {
        const selectedSegment = [
          this.parent.maskEnhancementsSection,
          this.parent.exfoliationSegment,
          this.parent.extractionsSegment,
          this.parent.sosSegment,
          this.parent.touchSegment,
          this.parent.maskSegment,
        ].filter((segment) => segment).length;
        return selectedSegment >= 1;
      },
    ),
    ...treatmentRecordCommonSchema.treatmentAttributes,
    ...facialTreatmentRecordCommonSchema,
  }),
  ...treatmentRecordCommonSchema.topLevel,
});
